import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Download, ExternalLink, Eye, FileText, Film, Image as ImageIcon, Inbox, Loader2 } from 'lucide-react';
import { sessionHubService, HubItem } from '../../services/sessionHub.service';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const size = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const typeOf = (contentType: string) =>
  contentType === 'text/html' ? { label: 'Report', icon: FileText }
    : contentType === 'application/pdf' ? { label: 'PDF', icon: FileText }
      : contentType.startsWith('video/') ? { label: 'Film', icon: Film }
        : contentType.startsWith('image/') ? { label: 'Image', icon: ImageIcon }
          : { label: 'File', icon: FileText };

type Viewing =
  | { item: HubItem; kind: 'html'; html: string }
  | { item: HubItem; kind: 'video' | 'image'; url: string };

/**
 * Reports render from srcdoc in a sandbox without same-origin, so their
 * scripts run but cannot reach this page, its storage or its token.
 */
function Viewer({ viewing, onClose, onDownload }: { viewing: Viewing; onClose: () => void; onDownload: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onClose} className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
          <ArrowLeft size={16} /> Back to shared items
        </button>
        <button
          type="button" onClick={onDownload}
          className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
        >
          <Download size={16} /> Download
        </button>
      </div>
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#e2e8f0]">
          <p className="text-sm font-semibold text-navy">{viewing.item.title}</p>
          <p className="text-xs text-slate-400">{viewing.item.sessionName}</p>
        </div>
        {viewing.kind === 'html' && (
          <iframe title={viewing.item.title} srcDoc={viewing.html} sandbox="allow-scripts allow-popups" className="w-full h-[75vh] bg-white" />
        )}
        {viewing.kind === 'video' && (
          <div className="p-3 sm:p-4"><video src={viewing.url} controls playsInline controlsList="nodownload" className="w-full rounded-xl bg-black aspect-video" /></div>
        )}
        {viewing.kind === 'image' && (
          <div className="p-4 flex justify-center bg-slate-50"><img src={viewing.url} alt={viewing.item.title} className="max-w-full max-h-[75vh] rounded-lg" /></div>
        )}
      </div>
    </div>
  );
}

export function SharedItems({ link, onCount, sessionId }: { link: string; onCount?: (n: number) => void; sessionId?: string }) {
  const [items, setItems] = useState<HubItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Viewing | null>(null);

  useEffect(() => {
    sessionHubService.items(link)
      .then((r) => {
        const list = sessionId ? r.items.filter((i) => i.sessionId === sessionId) : r.items;
        setItems(list);
        onCount?.(list.length);
      })
      .catch((e) => setError(e.message));
  }, [link, onCount, sessionId]);

  const download = useCallback(async (item: HubItem) => {
    setBusy(item.itemId);
    try {
      const r = await sessionHubService.itemLink(link, item.itemId, 'download');
      window.location.assign(r.url);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }, [link]);

  const view = useCallback(async (item: HubItem) => {
    setBusy(item.itemId);
    setError(null);
    try {
      const r = await sessionHubService.itemLink(link, item.itemId, 'view');
      if (item.contentType === 'text/html') {
        const res = await fetch(r.url, { credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!res.ok) throw new Error('We could not open this report. Please try again.');
        setViewing({ item, kind: 'html', html: await res.text() });
      } else if (item.contentType.startsWith('video/')) {
        setViewing({ item, kind: 'video', url: r.url });
      } else if (item.contentType.startsWith('image/')) {
        setViewing({ item, kind: 'image', url: r.url });
      } else {
        window.open(r.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }, [link]);

  if (viewing) return <Viewer viewing={viewing} onClose={() => setViewing(null)} onDownload={() => download(viewing.item)} />;

  if (error && !items) {
    return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>;
  }
  if (!items) {
    return <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4"><Inbox size={28} className="text-slate-400" /></div>
        <p className="text-slate-500 text-sm">Nothing has been shared here yet.</p>
        <p className="text-slate-400 text-xs mt-2">Your Version 1 team will let you know when it has.</p>
      </div>
    );
  }

  const bySession = items.reduce<Record<string, HubItem[]>>((acc, i) => ({ ...acc, [i.sessionName]: [...(acc[i.sessionName] ?? []), i] }), {});

  return (
    <div className="flex flex-col gap-6">
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>}
      {Object.entries(bySession).map(([sessionName, group]) => (
        <div key={sessionName} className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="px-5 pt-4 pb-2"><h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">{sessionName}</h2></div>
          <ul className="divide-y divide-[#e2e8f0]">
            {group.map((item) => {
              const t = typeOf(item.contentType);
              const opensInTab = item.contentType === 'application/pdf';
              return (
                <li key={item.itemId} className="px-5 py-4 flex flex-wrap items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><t.icon size={18} /></div>
                  <div className="flex-1 min-w-[12rem]">
                    <p className="text-sm font-semibold text-navy">{item.title}</p>
                    <p className="text-xs text-slate-400">{t.label}{item.sizeBytes ? ` · ${size(item.sizeBytes)}` : ''} · shared {fmtDate(item.publishedAt)}</p>
                    {item.description && <p className="text-xs text-slate-500 mt-1">{item.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button" disabled={busy === item.itemId} onClick={() => view(item)}
                      className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
                    >
                      {busy === item.itemId ? <Loader2 size={16} className="animate-spin" /> : opensInTab ? <ExternalLink size={16} /> : <Eye size={16} />} {opensInTab ? 'Open' : 'View'}
                    </button>
                    <button
                      type="button" disabled={busy === item.itemId} onClick={() => download(item)}
                      className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60"
                    >
                      <Download size={16} /> Download
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
